import { makeStyles } from '@material-ui/core'

export const useTableStyles = makeStyles((theme) => ({
  '@keyframes tableCellOpen': {
    '0%': {
      transform: 'scale(1, 0)',
      transformOrigin: 'top',
      maxHeight: 0
    },
    '100%': {
      transform: 'scale(1, 1)',
      transformOrigin: 'top',
      maxHeight: '2rem'
    }
  },
  '@keyframes fadey': {
    '0%': {
      opacity: 0.5
    },
    '100%': {
      opacity: 1
    }
  },
  glass: ({ depthStyles, singleLevel = false, glassFactor = 0.85 }) => ({
    padding: '0',
    display: 'block',
    minWidth: 0,
    '& .__table': {
      fontSize: '0.85rem',
      borderSpacing: 0,
      border: `1px solid color-mix(in srgb, var(--color-neutral-900) ${(glassFactor * 100) / 2}%, transparent)`,
      borderRadius: 'var(--border-radius-sm)',
      boxShadow: theme.layout.shadow.normal,
      '& .__thead': {
        position: 'sticky',
        top: ({ superHeaderRect }) =>
          superHeaderRect ? superHeaderRect.height : 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        borderBottom: `2px solid color-mix(in srgb, var(--color-neutral-900) ${(glassFactor * 100) / 2}%, transparent)`
      },
      '& .__th': {
        fontWeight: '800',
        fontSize: '0.875rem',
        padding: '8px 20px 8px 12px',
        userSelect: 'none',
        borderCollapse: 'separate',
        borderSpacing: 0,
        backgroundColor: `color-mix(in srgb, var(--tc-table-header-bg-color) ${glassFactor * 100}%, transparent)`,
        lineHeight: '30px',
        marginBottom: '-1px',
        '&.__right': {
          textAlign: 'right'
        },
        '&.__center': {
          textAlign: 'center'
        },
        '&.__left': {
          textAlign: 'left'
        },
        '&.__depth_1': {
          padding: '4px 20px 6px 12px',
          border: 'none'
        },
        '& .__th-content': {
          display: 'flex',
          alignItems: 'center'
        },
        '&:first-child': {
          borderTopLeftRadius: theme.layout.radius.tight
        },
        '&:last-child': {
          borderTopRightRadius: theme.layout.radius.tight
        }
      },
      '& .__tbody': {
        overflowX: 'hidden' // rec
      },
      '& .__tr': {
      },
      '& .__tr.divide': {
        borderTop: '1px solid var(--color-neutral-400)'
      },
      '& .__tr:last-child': {
        borderBottomLeftRadius: theme.layout.radius.tight,
        borderBottomRightRadius: theme.layout.radius.tight
      },
      '& .__tr:last-child .__td:first-child': {
        borderBottomLeftRadius: theme.layout.radius.tight
      },
      '& .__tr:last-child .__td:last-child': {
        borderBottomRightRadius: theme.layout.radius.tight
      },
      '& .__tr:hover .__tr-wrapper': {
        backgroundColor: `color-mix(in srgb, var(--color-primary-100) ${glassFactor * 100}%, transparent)`,
        transition: 'background-color .5s ease-in-out .2s'
      },
      '& .__tr.__child': {
        animation: '$tableCellOpen .2s ease-in-out'
      },
      '& .__tr.__depth_0 .__td': {
        fontWeight: singleLevel ? 'inherit' : '800',
        ...(depthStyles?.[0] ?? {})
      },
      '& .__tr.__depth_1': {
        '& .__td': depthStyles?.[1] ?? {},
        '& .__tr-wrapper > *:first-child': {
          paddingLeft: '18px'
        }
      },
      '& .__tr.__depth_2': {
        '& .__td': depthStyles?.[2] ?? {},
        '& .__tr-wrapper > *:first-child': {
          paddingLeft: '36px'
        }
      },
      '& .__tr.__depth_3': {
        '& .__td': depthStyles?.[3] ?? {},
        '& .__tr-wrapper > *:first-child': {
          paddingLeft: '54px'
        }
      },
      '& .__tr.__depth_4': {
        '& .__td': depthStyles?.[4] ?? {},
        '& .__tr-wrapper > *:first-child': {
          paddingLeft: '72px'
        }
      },
      '& .__td': {
        textAlign: 'left',
        padding: '6px 20px 6px 12px',
        fontWeight: '400',
        transition: 'background-color .2s ease-in-out 0s',
        '&.__right': {
          textAlign: 'right'
        },
        '& .__cell': {
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }
      },
      '& .__expand-container': {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        '& > *:first-child': {
          margin: '0 8px 0 0'
        },
        '& .__expand-item': {
          overflow: 'hidden'
        }
      },
      '& .__expand-handle': {
        animation: '$fadey .25s ease-in-out'
      },
      '& .__sort-icon': {
        padding: '0 0px 0 2px',
        '& .MuiSvgIcon-root': {
          transform: 'translateY(3px)'
        }
      },
      '& .__benchmark': {
        color: 'var(--text-color-main)',
        paddingTop: '4px',
        fontWeight: 'normal',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        width: '100%'
      }
    },
    '& .__sticky': {
      overflow: ({ hasHorizontalOverflow }) =>
        hasHorizontalOverflow ? 'auto' : 'unset',
      '& .__thead': {
        position: 'sticky',
        overflowX: 'unset !important',
        overflowY: 'unset !important',
        zIndex: 1,
        top: 0
      },
      '& .__tbody': {
        position: 'relative',
        overflowX: 'unset !important',
        zIndex: 0
      },
      '& [data-sticky-td]': {
        position: 'sticky !important'
      },
      '& [data-sticky-last-left-td]': {
        borderRight: '1px solid rgba(0,0,0,0.0)'
      },
      '& [data-sticky-first-right-td]': {
        borderLeft: '1px solid rgba(0,0,0,.1)'
      },
      '& .__tr.__depth_0 > .__tr-wrapper > .__td': {
        backgroundColor: `color-mix(in srgb, var(--tc-table-body-bg-color-d0) ${glassFactor * 100}%, transparent)`
      },
      '& .__tr.__depth_1 > .__tr-wrapper > .__td': {
        backgroundColor: `color-mix(in srgb, var(--tc-table-body-bg-color-d0) ${glassFactor * 100}%, transparent)`
      },
      '& .__tr.__depth_2 > .__tr-wrapper > *.__td': {
        backgroundColor: `color-mix(in srgb, var(--tc-table-body-bg-color-d0) ${glassFactor * 100}%, transparent)`
      },
      '& .__tr.__depth_3 > .__tr-wrapper > *.__td': {
        backgroundColor: `color-mix(in srgb, var(--tc-table-body-bg-color-d0) ${glassFactor * 100}%, transparent)`
      },
      '& .__tr.__depth_4 > .__tr-wrapper > *.__td': {
        backgroundColor: `color-mix(in srgb, var(--tc-table-body-bg-color-d0) ${glassFactor * 100}%, transparent)`
      }
    },
    '& .__color-icon': {
      minHeight: '12px',
      minWidth: '12px',
      maxWidth: '12px',
      maxHeight: '12px',
      marginRight: '5px',
      border: '.5px solid var(--color-neutral-600)'
    }
  }),
  presentationTable: ({ depthStyles }) => ({
    padding: '0',
    display: 'block',
    '& .__table': {
      borderSpacing: 0,
      '& .__thead': {
        position: 'sticky',
        top: ({ superHeaderRect }) => superHeaderRect ? superHeaderRect.height : 0,
        boxShadow: '0px 10px 6px -6px rgba(24, 27, 53, 0.06)',
        overflowY: 'auto',
        overflowX: 'hidden'
      },
      '& .__headerGroup': {
      },
      '& .__th': {
        fontWeight: '800',
        fontSize: '0.875rem',
        padding: '0.5rem 0.75rem',
        userSelect: 'none',
        backgroundColor: 'var(--tc-table-header-bg-color)',
        borderBottom: '2px solid #212945',
        lineHeight: '30px',
        '&.__right': {
          textAlign: 'right'
        },
        '&.__center': {
          textAlign: 'center'
        },
        '& .__th-content': {
          display: 'flex',
          alignItems: 'center'
        }
      },
      '& .__tf': {
        fontWeight: theme.typography.weights.bold,
        padding: '0 0.75rem',
        userSelect: 'none',
        backgroundColor: 'var(--tc-table-body-bg-color-d0)',
        borderTop: '2px solid var(--color-neutral-100)',
        lineHeight: '30px',
        '&.__right': {
          textAlign: 'right'
        },
        '&.__center': {
          textAlign: 'center'
        },
        '& .__th-content': {
          display: 'flex',
          alignItems: 'center'
        },
        '& .__td': {
          textAlign: 'left',
          padding: '8px 12px',
          fontWeight: '400',
          transition: 'background-color .2s ease-in-out 0s',
          '&.__right': {
            textAlign: 'right'
          },
          '&.__center': {
            textAlign: 'center'
          }
        }
      },

      '& .__tbody': {
        overflowX: 'hidden' // rec
      },
      '& .__tr': {
      },
      '& .__tr.divide': {
        borderTop: '1px solid var(--color-neutral-700)'
      },
      '& .__tr:hover .__tr-wrapper .__td': {
        backgroundColor: 'rgba(0, 0, 30, .1)',
        transition: 'background-color 3s ease-in-out .5s'
      },
      '& .__tr.__child': {
        animation: '$tableCellOpen .2s ease-in-out'
      },
      '& .__tr.__depth_0 .__td': {
        backgroundColor: 'var(--tc-table-body-bg-color-d0)',
        fontWeight: '800',
        ...(depthStyles?.[0] ?? {})
      },
      '& .__tr.__depth_1': {
        backgroundColor: 'var(--tc-table-body-bg-color-d1)',
        '& .__td': depthStyles?.[1] ?? {},
        '& .__td > *:first-child': {
          marginLeft: '18px'
        }
      },
      '& .__tr.__depth_2': {
        backgroundColor: 'var(--tc-table-body-bg-color-d2)',
        '& .__td': depthStyles?.[2] ?? {},
        '& .__td > *:first-child': {
          marginLeft: '36px'
        }
      },
      '& .__tr.__depth_3': {
        backgroundColor: 'var(--tc-table-body-bg-color-d3)',
        '& .__td': depthStyles?.[3] ?? {},
        '& .__td > *:first-child': {
          marginLeft: '54px'
        }
      },
      '& .__tr.__depth_4': {
        backgroundColor: 'var(--tc-table-body-bg-color-d4)',
        '& .__td': depthStyles?.[4] ?? {},
        '& .__td > *:first-child': {
          marginLeft: '72px'
        }
      },
      '& .__td': {
        textAlign: 'left',
        padding: '8px 12px',
        fontWeight: '400',
        transition: 'background-color .2s ease-in-out 0s',
        '&.__right': {
          textAlign: 'right'
        },
        '&.__center': {
          textAlign: 'center'
        }
      },
      '& .__expand-container': {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        '& > *:first-child': {
          margin: '0 4px 0 0'
        }
      },
      '& .__expand-handle': {
        animation: '$fadey .25s ease-in-out'
      },
      '& .__sort-icon': {
        padding: '0 2px',
        '& .MuiSvgIcon-root': {
          transform: 'translateY(3px)'
        }
      }
    },
    '& .__sticky': {
      overflow: 'auto',
      '& .__thead': {
        position: 'sticky',
        overflowX: 'unset !important',
        overflowY: 'unset !important',
        zIndex: 1,
        top: 0,
        boxShadow: '0px 3px 3px #ccc'
      },
      '& .__tbody': {
        position: 'relative',
        overflowX: 'unset !important',
        zIndex: 0
      },
      '& [data-sticky-td]': {
        position: 'sticky !important'
      },
      '& [data-sticky-last-left-td]': {
        borderRight: '1px solid rgba(0,0,0,.1)'
      },
      '& [data-sticky-first-right-td]': {
        borderLeft: '1px solid rgba(0,0,0,.1)'
      },
      '& .__tr.__depth_0 > .__tr-wrapper > .__td': {
        backgroundColor: 'var(--tc-table-body-bg-color-d0)'
      },
      '& .__tr.__depth_1 > .__tr-wrapper > .__td': {
        backgroundColor: 'var(--tc-table-body-bg-color-d0)'
      },
      '& .__tr.__depth_2 > .__tr-wrapper > *.__td': {
        backgroundColor: 'var(--tc-table-body-bg-color-d1)'
      },
      '& .__tr.__depth_3 > .__tr-wrapper > *.__td': {
        backgroundColor: 'var(--tc-table-body-bg-color-d2)'
      },
      '& .__tr.__depth_4 > .__tr-wrapper > *.__td': {
        backgroundColor: 'var(--tc-table-body-bg-color-d3)'
      }
    },
    '&.bordered': {
      '& .__thead': {
        border: '.5px solid var(--color-neutral-300)'
      },
      '& .__headerGroup .__th': {
        border: '.5px solid var(--color-neutral-300)'
      },
      '& .__headerGroup:not(:first-child) .__th': {
        backgroundColor: 'var(--tc-table-sub-header-bg-color)'
      }
    }
  }),
  sparkRoot: {
    opacity: ({ loading }) => loading ? 1 : 0,
    transition: ({ loading }) => loading ? 'opacity .25s ease-in-out .25s' : 'opacity .1s ease-in-out',
    height: '2px'
  }
}))
